/**
 * `/api/companies/*`. Companies are not behind the time tracking switch; any
 * member of the firm can read and edit them. The migration is admin-only.
 */
import axiosInstance from "../../../api/axiosConfig";
import { handleAPIError } from "../../../api/utils/errors";
import type {
  AcceptRequestBody,
  AcceptRequestResult,
  BillingPatch,
  CaseCompanyContext,
  CaseLinkPatch,
  CaseMatch,
  Company,
  CompanyApproval,
  CompanyApprovalKind,
  CompanyBilling,
  CompanyCaseRow,
  CompanyListRow,
  CompanyMessage,
  CompanyPatch,
  ContactLogin,
  FirmCaseRequest,
  ImportReport,
  LinkOptions,
  MigrationDecision,
  MigrationProposals,
  MigrationResult,
  NextExport,
  PlaybookPayload,
  PlaybookPreview,
  PlaybookRule,
  PlaybookRuleInput,
  RuleDraft
} from "../types";

const call = async <T>(run: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

type Saved = { company: Company; synced_cases?: string[] };

export const CompaniesAPI = {
  list: () =>
    call(() =>
      axiosInstance.get<{ companies: CompanyListRow[] }>("/companies")
    ).then((d) => d.companies),
  get: (id: string) =>
    call(() => axiosInstance.get<Saved>(`/companies/${id}`)).then(
      (d) => d.company
    ),
  create: (body: CompanyPatch & { individual_ids?: string[] }) =>
    call(() => axiosInstance.post<Saved>("/companies", body)).then(
      (d) => d.company
    ),
  update: (id: string, patch: CompanyPatch) =>
    call(() => axiosInstance.put<Saved>(`/companies/${id}`, patch)),
  cases: (id: string) =>
    call(() =>
      axiosInstance.get<{ cases: CompanyCaseRow[] }>(`/companies/${id}/cases`)
    ).then((d) => d.cases),
  link: (id: string, individualIds: string[], options: LinkOptions = {}) =>
    call(() =>
      axiosInstance.post<{ linked: string[] }>(`/companies/${id}/link`, {
        individual_ids: individualIds,
        ...options
      })
    ).then((d) => d.linked),
  unlink: (id: string, individualIds: string[]) =>
    call(() =>
      axiosInstance.post<{ unlinked: string[] }>(`/companies/${id}/unlink`, {
        individual_ids: individualIds
      })
    ).then((d) => d.unlinked),
  updateCase: (id: string, individualId: string, patch: CaseLinkPatch) =>
    call(() =>
      axiosInstance.put<CaseCompanyContext & { changed: string[] }>(
        `/companies/${id}/cases/${individualId}`,
        patch
      )
    ),
  forCase: (individualId: string) =>
    call(() =>
      axiosInstance.get<CaseCompanyContext>(
        `/companies/for-case/${individualId}`
      )
    ),
  caseMatches: (name: string) =>
    call(() =>
      axiosInstance.get<{ cases: CaseMatch[] }>("/companies/case-matches", {
        params: { name }
      })
    ).then((d) => d.cases),
  decideProposal: (
    id: string,
    proposalId: string,
    decision: "accept" | "dismiss"
  ) =>
    call(() =>
      axiosInstance.post<Saved>(
        `/companies/${id}/proposals/${proposalId}/${decision}`
      )
    ),
  importCsv: (csv: string, dryRun: boolean) =>
    call(() =>
      axiosInstance.post<ImportReport>("/companies/import", {
        csv,
        dry_run: dryRun
      })
    )
};

/** The company's playbook. Edits are for firm admins; reads for everyone. */
export const PlaybookAPI = {
  get: (id: string) =>
    call(() => axiosInstance.get<PlaybookPayload>(`/companies/${id}/playbook`)),
  /** Replace the live rules; suggestions not in the list stay. */
  save: (id: string, rules: PlaybookRuleInput[]) =>
    call(() =>
      axiosInstance.put<PlaybookPayload>(`/companies/${id}/playbook`, {
        rules
      })
    ),
  add: (id: string, rule: PlaybookRuleInput) =>
    call(() =>
      axiosInstance.post<PlaybookPayload & { rule: PlaybookRule }>(
        `/companies/${id}/playbook/rules`,
        rule
      )
    ),
  categorize: (id: string, text: string) =>
    call(() =>
      axiosInstance.post<{ draft: RuleDraft }>(
        `/companies/${id}/playbook/categorize`,
        { text }
      )
    ).then((d) => d.draft),
  decide: (
    id: string,
    ruleId: string,
    decision: "accept" | "dismiss",
    edits: PlaybookRuleInput = {}
  ) =>
    call(() =>
      axiosInstance.post<PlaybookPayload & { rule?: PlaybookRule }>(
        `/companies/${id}/playbook/${ruleId}/${decision}`,
        edits
      )
    ),
  /** Suggest rules read from the company's engagement letter and guidelines. */
  extract: (id: string, guidelineIds: string[]) =>
    call(() =>
      axiosInstance.post<
        PlaybookPayload & { suggested: PlaybookRule[]; skipped: number }
      >(`/companies/${id}/playbook/extract`, { guideline_ids: guidelineIds })
    ),
  preview: (id: string, individualId: string) =>
    call(() =>
      axiosInstance.get<PlaybookPreview>(
        `/companies/${id}/playbook/preview/${individualId}`
      )
    ),
  /** Turn one rule off (or back on) for one case. */
  setOverride: (individualId: string, ruleId: string, on: boolean) =>
    call(() =>
      axiosInstance.put<{
        individual_id: string;
        playbook_overrides: Record<string, boolean>;
      }>(`/individuals/${individualId}/playbook-overrides`, {
        rule_id: ruleId,
        on
      })
    )
};

export const CompanyMigrationAPI = {
  proposals: () =>
    call(() => axiosInstance.get<MigrationProposals>("/companies/migration")),
  apply: (decisions: MigrationDecision[], dryRun = false) =>
    call(() =>
      axiosInstance.post<{ companies: MigrationResult[] }>(
        "/companies/migration",
        { decisions, dry_run: dryRun }
      )
    ).then((d) => d.companies)
};

/** The company's billing terms. Edits are for firm admins. */
export const BillingAPI = {
  get: (id: string) =>
    call(() =>
      axiosInstance.get<{ billing: CompanyBilling }>(`/companies/${id}/billing`)
    ).then((d) => d.billing),
  save: (id: string, patch: BillingPatch) =>
    call(() =>
      axiosInstance.put<{ billing: CompanyBilling; changed_cases: string[] }>(
        `/companies/${id}/billing`,
        patch
      )
    ),
  /** Record that the firm sent the client this rate; CaseBlink never submits it. */
  markSubmitted: (id: string, userId: string) =>
    call(() =>
      axiosInstance.post<{ billing: CompanyBilling }>(
        `/companies/${id}/billing/rates/${userId}/submitted`
      )
    ).then((d) => d.billing),
  nextExport: (id: string) =>
    call(() =>
      axiosInstance.get<NextExport>(`/companies/${id}/billing/next-export`)
    )
};

/** The firm's side of the company's HR portal. */
export const PortalFirmAPI = {
  logins: (id: string) =>
    call(() =>
      axiosInstance.get<{ contacts: Record<string, ContactLogin> }>(
        `/companies/${id}/portal/contacts`
      )
    ).then((d) => d.contacts),
  invite: (id: string, contactId: string) =>
    call(() =>
      axiosInstance.post<{ status: string; email_sent: boolean }>(
        `/companies/${id}/portal/contacts/${contactId}/invite`
      )
    ),
  revoke: (id: string, contactId: string) =>
    call(() =>
      axiosInstance.post<{ revoked: number }>(
        `/companies/${id}/portal/contacts/${contactId}/revoke`
      )
    ),
  requests: (id: string) =>
    call(() =>
      axiosInstance.get<{ requests: FirmCaseRequest[] }>(
        `/companies/${id}/portal/requests`
      )
    ).then((d) => d.requests),
  accept: (id: string, requestId: string, body: AcceptRequestBody) =>
    call(() =>
      axiosInstance.post<AcceptRequestResult>(
        `/companies/${id}/portal/requests/${requestId}/accept`,
        body
      )
    ),
  decline: (id: string, requestId: string, reason: string) =>
    call(() =>
      axiosInstance.post<{ request: FirmCaseRequest }>(
        `/companies/${id}/portal/requests/${requestId}/decline`,
        { reason }
      )
    ).then((d) => d.request),
  ask: (id: string, requestId: string, text: string) =>
    call(() =>
      axiosInstance.post<{ request: FirmCaseRequest }>(
        `/companies/${id}/portal/requests/${requestId}/ask`,
        { text }
      )
    ).then((d) => d.request),
  approvals: (id: string, individualId?: string) =>
    call(() =>
      axiosInstance.get<{ approvals: CompanyApproval[] }>(
        `/companies/${id}/portal/approvals`,
        { params: individualId ? { individual_id: individualId } : {} }
      )
    ).then((d) => d.approvals),
  createApproval: (
    id: string,
    body: {
      individual_id: string;
      kind: CompanyApprovalKind;
      summary: string;
      amount?: number | null;
      contact_id?: string | null;
    }
  ) =>
    call(() =>
      axiosInstance.post<{ approval: CompanyApproval }>(
        `/companies/${id}/portal/approvals`,
        body
      )
    ).then((d) => d.approval),
  sendApproval: (id: string, approvalId: string) =>
    call(() =>
      axiosInstance.post<{ approval: CompanyApproval }>(
        `/companies/${id}/portal/approvals/${approvalId}/send`
      )
    ).then((d) => d.approval),
  cancelApproval: (id: string, approvalId: string) =>
    call(() =>
      axiosInstance.post<{ approval: CompanyApproval }>(
        `/companies/${id}/portal/approvals/${approvalId}/cancel`
      )
    ).then((d) => d.approval),
  messages: (id: string, individualId: string) =>
    call(() =>
      axiosInstance.get<{ messages: CompanyMessage[]; recipients: number }>(
        `/companies/${id}/portal/cases/${individualId}/messages`
      )
    ),
  reply: (id: string, individualId: string, body: string) =>
    call(() =>
      axiosInstance.post<{ message: CompanyMessage }>(
        `/companies/${id}/portal/cases/${individualId}/messages`,
        { body }
      )
    ).then((d) => d.message)
};
